import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlassCard } from '../components/GlassCard';
import { useUserStore } from '../store/userStore';
import { getLatestBiomarkerAnalysis } from '../lib/api';

const STATUS_COLOR = { normal: '#10B981', low: '#F59E0B', high: '#EF4444' };
const CONCERN_COLOR = { mild: '#F59E0B', moderate: '#F97316', significant: '#EF4444' };

export default function BiomarkerAnalysis() {
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    getLatestBiomarkerAnalysis(user.id).then(setA).finally(() => setLoading(false));
  }, [user?.id]);

  if (loading) return <div className="min-h-screen grid place-items-center" style={{ background: 'var(--aura-bg)' }}><i className="fa-solid fa-circle-notch fa-spin text-2xl text-[var(--aura-purple)]" /></div>;

  if (!a) {
    return (
      <div className="min-h-screen grid place-items-center px-4" style={{ background: 'var(--aura-bg)' }}>
        <GlassCard className="p-10 text-center max-w-md">
          <h2 className="font-display text-2xl mb-2">No analysis yet</h2>
          <p className="text-[var(--aura-ink-soft)] mb-5">Once your clinician adds your blood results, your AI analysis will appear here.</p>
          <button onClick={() => navigate('/biomarkers')} className="aura-btn-gradient text-white font-semibold rounded-full px-6 py-3">View my results</button>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--aura-bg)' }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-16">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/app')} className="grid place-items-center w-9 h-9 rounded-full bg-white border border-[var(--aura-line)]"><i className="fa-solid fa-arrow-left" /></button>
          <h1 className="font-display text-2xl sm:text-3xl">Biomarker analysis</h1>
        </div>

        <GlassCard className="p-6 mb-5">
          <div className="flex items-center gap-2 mb-2 text-[var(--aura-purple)]"><i className="fa-solid fa-microscope" /><span className="font-semibold">AI summary</span></div>
          <p className="text-[var(--aura-ink)] leading-relaxed">{a.summary}</p>
          {a.requires_review && (
            <div className="mt-3 flex items-center gap-2 text-sm text-amber-600 bg-amber-50 rounded-xl px-3 py-2">
              <i className="fa-solid fa-triangle-exclamation" /> Clinical review recommended
            </div>
          )}
        </GlassCard>

        {a.flags?.length > 0 && (
          <div className="mb-5">
            <h3 className="font-display text-xl mb-3">Flagged markers</h3>
            <div className="flex flex-col gap-3">
              {a.flags.map((fl, i) => (
                <GlassCard key={i} className="p-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold">{fl.marker}</span>
                    <span className="text-xs font-semibold rounded-full px-2.5 py-1 text-white capitalize" style={{ background: CONCERN_COLOR[fl.concern_level] || '#F59E0B' }}>
                      {fl.status} · {fl.concern_level}
                    </span>
                  </div>
                  <p className="text-sm text-[var(--aura-ink-soft)]">{fl.explanation}</p>
                </GlassCard>
              ))}
            </div>
          </div>
        )}

        {a.detailed_analysis?.length > 0 && (
          <div className="mb-5">
            <h3 className="font-display text-xl mb-3">Per-marker breakdown</h3>
            <div className="flex flex-col gap-3">
              {a.detailed_analysis.map((d, i) => (
                <GlassCard key={i} className="p-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold">{d.marker} <span className="text-[var(--aura-ink-soft)] font-normal">{d.value}</span></span>
                    <span className="text-xs font-semibold capitalize" style={{ color: STATUS_COLOR[d.status] || '#10B981' }}>{d.status}</span>
                  </div>
                  <p className="text-sm text-[var(--aura-ink-soft)]">{d.explanation}</p>
                  {d.significance && <p className="text-sm text-[var(--aura-teal)] mt-1">{d.significance}</p>}
                </GlassCard>
              ))}
            </div>
          </div>
        )}

        {a.symptom_connections?.length > 0 && (
          <div className="mb-5">
            <h3 className="font-display text-xl mb-3">Symptom connections</h3>
            <div className="flex flex-col gap-3">
              {a.symptom_connections.map((s, i) => (
                <GlassCard key={i} className="p-4">
                  <div className="font-semibold mb-1"><i className="fa-solid fa-link mr-2 text-[var(--aura-purple)]" />{s.symptom}</div>
                  <p className="text-sm text-[var(--aura-ink-soft)]"><span className="font-medium">{s.related_markers}</span> — {s.explanation}</p>
                </GlassCard>
              ))}
            </div>
          </div>
        )}

        {a.lifestyle_recommendations?.length > 0 && (
          <div className="mb-5">
            <h3 className="font-display text-xl mb-3">Lifestyle recommendations</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {a.lifestyle_recommendations.map((r, i) => (
                <GlassCard key={i} className="p-4">
                  <div className="text-xs uppercase tracking-wide text-[var(--aura-teal)] font-semibold mb-1">{r.category}</div>
                  <div className="font-medium text-sm mb-1">{r.recommendation}</div>
                  <p className="text-xs text-[var(--aura-ink-soft)]">{r.rationale}</p>
                </GlassCard>
              ))}
            </div>
          </div>
        )}

        {a.clinical_notes && (
          <GlassCard className="p-5 mb-6">
            <h3 className="font-display text-lg mb-2">Clinical notes</h3>
            <p className="text-sm text-[var(--aura-ink-soft)]">{a.clinical_notes}</p>
          </GlassCard>
        )}

        <p className="text-xs text-center text-[var(--aura-ink-soft)]">This analysis supports but does not replace clinical consultation.</p>
      </div>
    </div>
  );
}
